import {
  EmailBuilderState,
  EmailTemplateKey,
  buildDefaultEmailBuilderState,
  cloneEmailBuilderState,
} from "./emailBuilderData";

export type EmailStatus =
  | "draft"
  | "review"
  | "scheduled"
  | "sending"
  | "sent"
  | "canceled"
  | "failed";

export type NewsroomStatus = "notPublished" | "published" | "unpublished";

// The eight communication types offered by the Step-1 mock, plus the legacy
// "bulletin" value still present on older saved records. The backend
// CmpNewsletterTypeEnum only covers newsletter / pressRelease / bulletin /
// eBlast / announcement, so the four newer types persist as NEWSLETTER until
// the enum is extended (see emailTypeToApi).
export type EmailType =
  | "newsletter"
  | "eBlast"
  | "advocacyUpdate"
  | "pressRelease"
  | "announcement"
  | "memberWelcome"
  | "fromTheDesk"
  | "customEmail"
  | "bulletin";

export type RecipientSourceId =
  | "activeMembers"
  | "allMembers"
  | "nonMemberSubscribers"
  | "newMembers"
  | "renewingMembers"
  | "prospectMembers"
  | "eventAttendees"
  | "pastEventAttendees"
  | "eblastSubscribers"
  | "directRecipients"
  | "selectedMembers";

export const nonMemberSubscribersRecipientSourceLabel =
  "Non Member Subscribers (Including website subscribers)";

export type NewsletterMemberFilters = {
  membershipTypeIds: string[];
  employeeRangeIds: string[];
  categoryIds: string[];
  memberSinceRangeIds: string[];
  renewalRangeIds: string[];
};

export const buildDefaultNewsletterMemberFilters =
  (): NewsletterMemberFilters => ({
    membershipTypeIds: [],
    employeeRangeIds: [],
    categoryIds: [],
    memberSinceRangeIds: [],
    renewalRangeIds: [],
  });

// Step 3 template selection. "" = no template chosen yet (gallery phase);
// the 8 template keys and their seeded blocks live in emailBuilderData
// (emailTemplatePresets).
export type EmailTemplateSelection = EmailTemplateKey | "";

export type EmailAttachment = {
  id: string;
  name: string;
  fileType: string;
  sizeBytes: number;
  sizeLabel: string;
  storageKey?: string;
  eTag?: string;
};

export type EmailOutboundStatus = "sent" | "failed" | "skipped";

export type EmailOutboundRecord = {
  id: string;
  kind: string;
  status: EmailOutboundStatus;
  toEmail: string;
  intendedToEmail: string;
  recipientOverrideUsed: boolean;
  subject: string;
  errorMessage: string;
  attachmentCount: number;
  createdAt: string;
  sentAt: string;
  failedAt: string;
  openedAt: string;
  clickedAt: string;
};

export type EmailDeliverySummary = {
  totalOutboundEmailCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  intendedRecipientCount: number;
  actualRecipientCount: number;
  recipientOverrideUsed: boolean;
  latestOutboundEmailAt: string;
};

export type RecipientGroup = {
  id: string;
  name: string;
  description: string;
  count: number;
  source: string;
  isArchived?: boolean;
};

export type EmailAudienceMemberOption = {
  id: string;
  companyName: string;
  lifecycleStatus: string;
  membershipTypeId: string;
  membershipTypeName: string;
  dateJoinedUtcMilli?: number | null;
  fullTimeEmployees?: number | null;
  partTimeEmployees?: number | null;
  renewalDateUtcMilli?: number | null;
  categoryId?: string[] | null;
};

export type RecipientPreviewSummary = {
  totalCandidateCount: number;
  uniqueValidRecipientCount: number;
  invalidRecipientCount: number;
  skippedRecipientCount: number;
  recipientOverrideUsed: boolean;
  actualTestRecipientEmail: string;
  recipientRoutingMode: string;
};

export type EmailFormState = {
  id?: string;
  status: EmailStatus;
  subject: string;
  preheader: string;
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  emailType: EmailType;
  category: string;
  tags: string;
  recipientSourceIds: RecipientSourceId[];
  memberFilters: NewsletterMemberFilters;
  recipientGroupIds: string[];
  selectedMemberIds: string[];
  directRecipients: string;
  templateId: EmailTemplateSelection;
  headerImageName: string;
  content: string;
  builderState: EmailBuilderState;
  attachments: EmailAttachment[];
  publishToNewsroom: boolean;
  newsroomStatus: NewsroomStatus;
  newsroomSlug: string;
  seoTitle: string;
  seoDescription: string;
  scheduledDate: string;
  scheduledTime: string;
  reviewNote: string;
  estimatedRecipientCount: number;
  invalidRecipientCount: number;
  skippedRecipientCount: number;
  sentRecipientCount: number;
  failedRecipientCount: number;
  skippedDeliveryCount: number;
  openCount: number;
  clickCount: number;
  outboundEmails: EmailOutboundRecord[];
  deliverySummary: EmailDeliverySummary | null;
  senderReadinessLabel: string;
  senderReadinessDetail: string;
};

export type EmailCampaign = {
  id: string;
  slug: string;
  subject: string;
  emailType: EmailType;
  category: string;
  status: EmailStatus;
  newsroomStatus: NewsroomStatus;
  scheduledAt?: string;
  sentAt?: string;
  publishedAt?: string;
  intendedRecipients: number;
  invalidRecipients: number;
  skippedCandidateRecipients: number;
  sentRecipients: number;
  skippedRecipients: number;
  failedRecipients: number;
  skippedDeliveries: number;
  openCount: number;
  clickCount: number;
  attachmentCount: number;
  updatedAt: string;
};

// Labels/order match the Step-1 "Choose the Type of Communication" cards
// verbatim; "bulletin" stays last as a legacy value for older records.
export const emailTypeOptions: Array<{ value: EmailType; label: string }> = [
  { value: "newsletter", label: "Newsletter" },
  { value: "eBlast", label: "E-Blast" },
  { value: "advocacyUpdate", label: "Advocacy Update" },
  { value: "pressRelease", label: "Media Release" },
  { value: "announcement", label: "Event Announcement" },
  { value: "memberWelcome", label: "Member Welcome" },
  { value: "fromTheDesk", label: "From the Desk" },
  { value: "customEmail", label: "Custom Email" },
  { value: "bulletin", label: "Chamber Bulletin" },
];

export const buildDefaultCreateEmailForm = ({
  chamberName,
  senderEmail,
  replyToEmail,
}: {
  chamberName?: string;
  senderEmail?: string;
  replyToEmail?: string;
} = {}): EmailFormState => ({
  status: "draft",
  subject: "",
  preheader: "",
  fromName: chamberName || "Chamber of Commerce",
  fromEmail: senderEmail || "",
  replyToEmail: replyToEmail || senderEmail || "",
  emailType: "newsletter",
  // Pristine per the Step-1/Step-2 mocks: no audience pre-checked and no
  // fabricated campaign name/tags saved onto real records.
  category: "",
  tags: "",
  recipientSourceIds: [],
  memberFilters: buildDefaultNewsletterMemberFilters(),
  recipientGroupIds: [],
  selectedMemberIds: [],
  directRecipients: "",
  // Empty until the user picks one of the 8 templates in the Step 3 gallery.
  templateId: "",
  headerImageName: "",
  content: "",
  builderState: buildDefaultEmailBuilderState(chamberName),
  attachments: [],
  publishToNewsroom: false,
  newsroomStatus: "notPublished",
  newsroomSlug: "",
  seoTitle: "",
  seoDescription: "",
  scheduledDate: "",
  scheduledTime: "",
  reviewNote: "",
  estimatedRecipientCount: 0,
  invalidRecipientCount: 0,
  skippedRecipientCount: 0,
  sentRecipientCount: 0,
  failedRecipientCount: 0,
  skippedDeliveryCount: 0,
  openCount: 0,
  clickCount: 0,
  outboundEmails: [],
  deliverySummary: null,
  senderReadinessLabel: "Not checked yet",
  senderReadinessDetail: "Save the draft to check sender-domain readiness.",
});

export const cloneEmailForm = (form: EmailFormState): EmailFormState => ({
  ...form,
  recipientSourceIds: [...form.recipientSourceIds],
  memberFilters: {
    membershipTypeIds: [...form.memberFilters.membershipTypeIds],
    employeeRangeIds: [...form.memberFilters.employeeRangeIds],
    categoryIds: [...form.memberFilters.categoryIds],
    memberSinceRangeIds: [...form.memberFilters.memberSinceRangeIds],
    renewalRangeIds: [...form.memberFilters.renewalRangeIds],
  },
  recipientGroupIds: [...form.recipientGroupIds],
  selectedMemberIds: [...form.selectedMemberIds],
  builderState: cloneEmailBuilderState(form.builderState),
  attachments: form.attachments.map((attachment) => ({ ...attachment })),
  outboundEmails: form.outboundEmails.map((outboundEmail) => ({
    ...outboundEmail,
  })),
  deliverySummary: form.deliverySummary ? { ...form.deliverySummary } : null,
});

export const getEmailTypeLabel = (emailType: EmailType) =>
  emailTypeOptions.find((option) => option.value === emailType)?.label ||
  emailType;

export const getStatusLabel = (status: EmailStatus) => {
  switch (status) {
    case "review":
      return "Ready for Review";
    case "scheduled":
      return "Scheduled";
    case "sending":
      return "Sending";
    case "sent":
      return "Sent";
    case "canceled":
      return "Canceled";
    case "failed":
      return "Failed";
    case "draft":
    default:
      return "Draft";
  }
};

export const getNewsroomStatusLabel = (status: NewsroomStatus) => {
  switch (status) {
    case "published":
      return "Published";
    case "unpublished":
      return "Unpublished";
    case "notPublished":
    default:
      return "Not Published";
  }
};
